import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { http } from '../../services/http';
import type { LoginResponse, UserDataResponse } from '../../types/auth';
import { getApiGatewayUrl, getApiTimeout } from '../../config.js';

// Create a separate axios instance for login without auth interceptors
const loginClient = axios.create({
  baseURL: getApiGatewayUrl() || import.meta.env.VITE_API_URL,
  timeout: getApiTimeout(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add response interceptor to handle errors
loginClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data) {
      // Parse the response data if it's a string
      if (typeof response.data === 'string') {
        try {
          response.data = JSON.parse(response.data);
        } catch (e) {
          // If parsing fails, keep original response
        }
      }

      // Extract sessionId from headers if present
      const sessionId = response.headers['sessionid'];
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId);
      }

      return response;
    }
    return Promise.reject({
      message: 'No data received from server'
    });
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject({
      message: error.response?.data?.message || 'An error occurred during the request'
    });
  }
);

// Add request interceptor to include sessionId
loginClient.interceptors.request.use(
  (config) => {
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      config.headers['SessionID'] = sessionId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export class LoginService {
  async GetRoles(username: string, password: string): Promise<LoginResponse> {
    try {
      console.log('Requesting GetRoles for user:', username);
      const response = await loginClient.get(`/PlatformCoreServices/Authorize/Login/GetRoles?EmailId=${encodeURIComponent(username)}&Password=${encodeURIComponent(password)}`);
      console.log('GetRoles response:', response);
      console.log('Response headers:', response.headers);
      return response.data;
    } catch (error) {
      console.error('GetRoles error:', error);
      throw error;
    }
  }

  async Login(userId: number, applicationType: number): Promise<UserDataResponse> {
    try {
      console.log('Requesting Login for userId:', userId);
      const response = await loginClient.get(`/PlatformCoreServices/Authorize/Login/GetLoginVerification?UserId=${userId}&ModuleId=${applicationType}`);
      console.log('Login response:', response);
      console.log('Response headers:', response.headers);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Checks whether the given email belongs to an active, non-deleted user.
  // Used by the Forgot Password flow before directing the user to their admin.
  async CheckEmailExists(emailId: string): Promise<{ UserId: number; msg: 'Exists' | 'NotExists' } | null> {
    try {
      const response = await loginClient.get(
        `/PlatformCoreServices/Authorize/Password/GetUserMailId?EmailId=${encodeURIComponent(emailId)}`
      )
      const list = response.data?.result
      return Array.isArray(list) && list.length > 0 ? list[0] : null
    } catch (error) {
      console.error('CheckEmailExists error:', error)
      throw error
    }
  }
}

export default new LoginService();
